import { getCollection } from "@/api/inventory/getConfig";
import { useUpdatePricesMutation } from "@/api/inventory/price";
import { usePersistentForm } from "@/hooks/usePersistForm";
import {
	ActionIcon,
	Button,
	Group,
	NumberInput,
	Stack,
	Table,
	Text,
} from "@mantine/core";
import { IconReload, IconTrash } from "@tabler/icons-react";
import { useStore } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

export const Route = createFileRoute("/_auth/$collection/update-price")({
	loader: async ({ params, context }) => {
		const collection = await getCollection({
			queryClient: context.queryClient,
			collectionAddress: params.collection,
		});
		return collection;
	},
	component: UpdatePrice,
});

const mintEntrySchema = z.object({
	tokenId: z.number().min(0, "Token ID must be positive"),
	priceInUsd: z.number().gt(0, "Price must be positive"),
	sellbackPriceInUsd: z.number().gt(0, "Price must be positive"),
	sellbackPriceInCredits: z.number().gt(0, "Price must be positive"),
});

const formSchema = z.object({
	entries: z.array(mintEntrySchema),
});

type FormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: FormValues = {
	entries: [
		{
			tokenId: 0,
			priceInUsd: 0,
			sellbackPriceInUsd: 0,
			sellbackPriceInCredits: 0,
		},
	],
};

export function UpdatePrice() {
	const collection = Route.useLoaderData();

	const priceMutation = useUpdatePricesMutation();

	const form = usePersistentForm(
		`form${window.location.pathname.replace(/\//g, "-")}`,
		{
			defaultValues: DEFAULT_VALUES,
			validators: {
				onSubmit: formSchema,
			},
			onSubmit: async ({ value }) => {
				await priceMutation.mutateAsync({
					collectionAddress: collection.address,
					prices: value.entries.map((entry) => ({
						tokenId: entry.tokenId,
						priceInUsd: entry.priceInUsd,
						sellbackPriceInCredits: entry.sellbackPriceInCredits,
						sellbackPriceInUsd: entry.sellbackPriceInUsd,
					})),
				});
			},
		},
	);

	const canSubmit = useStore(form.store, (state) => state.canSubmit);
	const isSubmitting = priceMutation.isPending;
	const isSubmitted = priceMutation.isSuccess;

	const addNewEntry = () => {
		const currentEntries = form.getFieldValue("entries");
		const firstEntry = currentEntries[0];
		const newEntry = {
			tokenId: firstEntry.tokenId + 1,
			priceInUsd: firstEntry.priceInUsd,
			sellbackPriceInCredits: firstEntry.sellbackPriceInCredits,
			sellbackPriceInUsd: firstEntry.sellbackPriceInUsd,
		};
		form.reset({
			entries: [newEntry, ...currentEntries],
		});
	};

	const removeEntry = (index: number) => {
		const currentEntries = form.getFieldValue("entries");
		form.reset({
			entries: currentEntries.filter((_, i) => i !== index),
		});
	};

	const entries = useStore(form.store, (state) => state.values.entries);

	return (
		<Stack gap="md" p="md" maw={900} mx="auto">
			<Group justify="space-between">
				<Text size="xl" fw={700}>
					Update NFT Prices
				</Text>
				<Group gap="sm">
					<Button
						variant="subtle"
						leftSection={<IconReload size="1.2rem" />}
						onClick={() => {
							form.reset(DEFAULT_VALUES);
							priceMutation.reset();
						}}
					>
						Reset Form
					</Button>
					<Button
						variant="light"
						onClick={addNewEntry}
						disabled={isSubmitting || isSubmitted}
					>
						Add Token
					</Button>
					{isSubmitted ? (
						<Button
							onClick={() => {
								priceMutation.reset();
							}}
						>
							Modify Again
						</Button>
					) : (
						<Button
							onClick={() => form.handleSubmit()}
							loading={isSubmitting}
							disabled={!canSubmit || isSubmitting || isSubmitted}
						>
							Update Prices
						</Button>
					)}
				</Group>
			</Group>

			<div style={{ overflowX: "auto", width: "100%" }}>
				<Table style={{ minWidth: "800px" }}>
					<Table.Thead>
						<Table.Tr>
							<Table.Th style={{ minWidth: "100px", width: "120px" }}>
								Token ID
							</Table.Th>
							<Table.Th style={{ minWidth: "100px" }}>Price in USD</Table.Th>
							<Table.Th style={{ minWidth: "100px" }}>Sellback USD</Table.Th>
							<Table.Th style={{ minWidth: "100px" }}>
								Sellback Credits
							</Table.Th>
							<Table.Th style={{ minWidth: "40px", width: "40px" }} />
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{entries.map((_, index) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
							<Table.Tr key={index}>
								<Table.Td>
									<form.Field name={`entries[${index}].tokenId`}>
										{(field) => (
											<NumberInput
												value={field.state.value}
												onChange={(value) => field.handleChange(Number(value))}
												onBlur={field.handleBlur}
												required
												allowDecimal={false}
												min={0}
												placeholder="Token ID"
												step={1}
												thousandSeparator=","
												error={field.state.meta.errors[0]?.toString()}
												disabled={isSubmitting || isSubmitted}
											/>
										)}
									</form.Field>
								</Table.Td>
								<Table.Td>
									<form.Field name={`entries[${index}].priceInUsd`}>
										{(field) => (
											<NumberInput
												flex={1}
												value={field.state.value ?? undefined}
												onChange={(value) => field.handleChange(Number(value))}
												onBlur={field.handleBlur}
												min={0}
												step={0.1}
												decimalScale={2}
												fixedDecimalScale
												prefix="$"
												placeholder="Price In USD"
												thousandSeparator=","
												error={!!field.state.meta.errors[0]?.toString()}
												disabled={isSubmitting || isSubmitted}
											/>
										)}
									</form.Field>
								</Table.Td>
								<Table.Td>
									<form.Field name={`entries[${index}].sellbackPriceInUsd`}>
										{(field) => (
											<NumberInput
												flex={1}
												value={field.state.value ?? undefined}
												onChange={(value) => field.handleChange(Number(value))}
												onBlur={field.handleBlur}
												min={0}
												step={0.1}
												decimalScale={2}
												fixedDecimalScale
												prefix="$"
												placeholder="Sellback USD"
												thousandSeparator=","
												error={!!field.state.meta.errors[0]?.toString()}
												disabled={isSubmitting || isSubmitted}
											/>
										)}
									</form.Field>
								</Table.Td>
								<Table.Td>
									<form.Field name={`entries[${index}].sellbackPriceInCredits`}>
										{(field) => (
											<NumberInput
												flex={1}
												value={field.state.value ?? undefined}
												onChange={(value) => field.handleChange(Number(value))}
												onBlur={field.handleBlur}
												min={0}
												step={0.1}
												decimalScale={2}
												fixedDecimalScale
												prefix="$"
												placeholder="Sellback Credits"
												thousandSeparator=","
												error={!!field.state.meta.errors[0]?.toString()}
												disabled={isSubmitting || isSubmitted}
											/>
										)}
									</form.Field>
								</Table.Td>
								<Table.Td>
									<ActionIcon
										color="red"
										variant="subtle"
										onClick={() => removeEntry(index)}
										disabled={
											entries.length === 1 || isSubmitting || isSubmitted
										}
									>
										<IconTrash size="1.2rem" />
									</ActionIcon>
								</Table.Td>
							</Table.Tr>
						))}
					</Table.Tbody>
				</Table>
			</div>
		</Stack>
	);
}
